import React from 'react';
import { Modal, Button, Icon, List } from 'semantic-ui-react';

/**
 * ConfirmModal — an in-app confirmation, in place of window.confirm.
 *
 * WHY NOT window.confirm. Three reasons, in order of how much they matter.
 *
 * It cannot be read properly. A native confirm renders one run of plain text, so a list of the
 * books about to be destroyed arrives as bullet characters and newlines jammed into a paragraph.
 * The whole point of naming them is that they can be scanned.
 *
 * It cannot be styled, so nothing distinguishes "remove this draft record" from "destroy a
 * published group, its audio and its files". Both arrive as the same grey box with the same OK
 * button, and the second one is not undoable.
 *
 * And it is not ours — it carries the browser's chrome and the page's URL, which in a tool that
 * has just spent ten seconds deleting things reads as something the site did not intend.
 *
 * DELIBERATELY NOT A DROP-IN REPLACEMENT for every confirm. This one exists for the destructive
 * end of the range, where naming the casualties and colouring the button is the point.
 *
 * Props:
 *   open          — boolean
 *   title         — heading
 *   icon          — heading icon (default 'warning sign')
 *   children      — body text / anything
 *   items         — optional string[] rendered as a scannable list (the books, the characters…)
 *   note          — optional smaller line under the list, for the consequence
 *   confirmLabel  — button text (default 'Delete')
 *   danger        — red confirm button and red heading icon (default true)
 *   loading       — disables both buttons and spins the confirm
 *   onConfirm     — ()
 *   onCancel      — ()
 *   size          — Modal size (default 'tiny'). ⭐ A dialog the operator WRITES in needs room;
 *                   a dialog they only read does not. Everything existing keeps the small one.
 */
const ConfirmModal = ({
  open,
  title,
  icon = 'warning sign',
  children,
  items,
  note,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  danger = true,
  loading = false,
  onConfirm,
  onCancel,
  size = 'tiny',
}) => (
  <Modal open={!!open} size={size} onClose={loading ? undefined : onCancel} closeOnDimmerClick={!loading}>
    <Modal.Header>
      <Icon name={icon} color={danger ? 'red' : 'blue'} /> {title}
    </Modal.Header>
    <Modal.Content>
      {children}
      {/* The casualties, one per line. This is the reason the modal exists — in a native confirm
          these collapse into a paragraph and stop being readable at exactly the moment they
          matter most. */}
      {items && items.length > 0 && (
        <List bulleted style={{ marginTop: '0.8em', marginBottom: '0.8em' }}>
          {items.map((it) => (
            <List.Item key={it} style={{ fontWeight: 600 }}>{it}</List.Item>
          ))}
        </List>
      )}
      {note && (
        <p style={{ color: '#767676', fontSize: '0.95em', marginBottom: 0 }}>{note}</p>
      )}
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onCancel} disabled={loading}>{cancelLabel}</Button>
      <Button
        color={danger ? 'red' : 'blue'}
        onClick={onConfirm}
        loading={loading}
        disabled={loading}
      >
        <Icon name={danger ? 'trash alternate' : 'check'} /> {confirmLabel}
      </Button>
    </Modal.Actions>
  </Modal>
);

export default ConfirmModal;
